import React from 'react';
import { useSeller } from '@reverbdotcom/commons/src/user_hooks';
import { useHelpCenterArticleUrl, useHelpCenterSectionUrl } from '@reverbdotcom/commons/src/url_helpers';
import { RCCallout, useMediaQuery } from '@reverbdotcom/cadence/components';
import royalMailLogo from '../../images/icons/shipping-icons/royal-mail.svg';
import { QuestionIcon } from '@reverbdotcom/cadence/icons/react';
import { COUNTRY_CODES } from '@reverbdotcom/commons/src/constants';
import { withPrefix } from '@reverbdotcom/commons/src/i18n_helpers';
import { I18N } from '@reverbdotcom/commons/src/components/translate';
import { PackageIcon } from '@phosphor-icons/react';

const SHIPPING_HELP_SECTION_ID = '40908908747803';
const REVERB_LABELS_HELP_ARTICLE_ID = '41988323612955';
const MANUAL_TRACKING_HELP_ARTICLE_ID = '41988420768667';
const REVERB_PAYMENTS_HELP_ARTICLE_ID = '40917640416027';
const PAYPAL_CONNECT_HELP_ARTICLE_ID = '41988432315163';
const PAYPAL_PAYMENT_HELP_ARTICLE_ID = '41988411420699';

const ORDERS_SHIPPING_BANNER_DISMISSED_KEY = 'orders_shipping_banner_dismissed';
const ORDERS_PAYMENT_BANNER_DISMISSED_KEY = 'orders_payment_banner_dismissed';

const t = withPrefix('discovery.ordersBanners');

function linkTemplate(href) {
  return `<a href=${href} target="_blank">`;
}

function shippingTitle({ shopCountryCode, isMobile, isUKReverbShipping } ) {
  if (isMobile) return undefined;

  if (isUKReverbShipping) {
    return t('shippingUKTitle');
  } else if (shopCountryCode === COUNTRY_CODES.US) {
    return t('shippingUSTitle');
  } else {
    return t('shippingTitle');
  }
}

function USShippingContent() {
  const learnLink = useHelpCenterSectionUrl(SHIPPING_HELP_SECTION_ID);

  return <I18N
    html
    text="discovery.ordersBanners.shippingUSContent"
    args={{
      labelsLinkOpen: linkTemplate('https://reverb.com/selling/shipping-labels'),
      guidesLinkOpen: linkTemplate('https://reverb.com/selling/shippingguide'),
      suppliesLinkOpen: linkTemplate('https://reverb.com/shop/merch'),
      learnLinkOpen: linkTemplate(learnLink),
      linkClose: '</a>',
    }}
  />;
}

function UKShippingContent() {
  const learnLink = useHelpCenterArticleUrl(REVERB_LABELS_HELP_ARTICLE_ID);

  return <I18N
    html
    text="discovery.ordersBanners.shippingUKContent"
    args={{
      learnLinkOpen: linkTemplate(learnLink),
      linkClose: '</a>',
    }}
  />;
}

function ManualShippingContent() {
  const learnLink = useHelpCenterArticleUrl(MANUAL_TRACKING_HELP_ARTICLE_ID);

  return <I18N
    html
    text="discovery.ordersBanners.shippingContent"
    args={{
      learnLinkOpen: linkTemplate(learnLink),
      linkClose: '</a>',
    }}
  />;
}

function useMobileShippingHelpUrl({ shopCountryCode, isUKReverbShipping }) {
  const shippingUrl = useHelpCenterSectionUrl(SHIPPING_HELP_SECTION_ID);
  const reverbLabelsUrl = useHelpCenterArticleUrl(REVERB_LABELS_HELP_ARTICLE_ID);
  const manualTrackingUrl = useHelpCenterArticleUrl(MANUAL_TRACKING_HELP_ARTICLE_ID);

  if (isUKReverbShipping) {
    return reverbLabelsUrl;
  } else if (shopCountryCode === COUNTRY_CODES.US) {
    return shippingUrl;
  } else {
    return manualTrackingUrl;
  }
}

function ShippingContentMobile({ shopCountryCode, isUKReverbShipping }) {
  const learnUrl = useMobileShippingHelpUrl({ shopCountryCode, isUKReverbShipping });

  const eligibleForReverbShipping = shopCountryCode === COUNTRY_CODES.US || isUKReverbShipping;
  const translationKey = eligibleForReverbShipping ? 'reverbShippingTitleMobile' : 'manualShippingTitleMobile';

  return <I18N
    html
    text={`discovery.ordersBanners.${translationKey}`}
    args={{
      learnLinkOpen: linkTemplate(learnUrl),
      linkClose: '</a>',
    }}
  />;
}

function ShippingContentDesktop({ shopCountryCode, isUKReverbShipping }) {
  if (isUKReverbShipping) {
    return <UKShippingContent />;
  } else if (shopCountryCode === COUNTRY_CODES.US) {
    return <USShippingContent />;
  } else {
    return <ManualShippingContent />;
  }
}

function ShippingContent({ shopCountryCode, isMobile, isUKReverbShipping }) {
  if (isMobile) {
    return <ShippingContentMobile shopCountryCode={shopCountryCode} isUKReverbShipping={isUKReverbShipping} />;
  } else {
    return <ShippingContentDesktop shopCountryCode={shopCountryCode} isUKReverbShipping={isUKReverbShipping} />;
  }
}

function ReverbPaymentsContent() {
  const learnUrl = useHelpCenterArticleUrl(REVERB_PAYMENTS_HELP_ARTICLE_ID);

  return <I18N
    html
    text="discovery.ordersBanners.reverbPaymentsContent"
    args={{
      learnLinkOpen: linkTemplate(learnUrl),
      linkClose: '</a>',
    }}
  />;
}

function PayPalPaymentContent() {
  const payPalConnectUrl = useHelpCenterArticleUrl(PAYPAL_CONNECT_HELP_ARTICLE_ID);
  const payPalLearnUrl = useHelpCenterArticleUrl(PAYPAL_PAYMENT_HELP_ARTICLE_ID);

  return <I18N
    html
    text="discovery.ordersBanners.payPalPaymentContent"
    args={{
      ppConnectLinkOpen: linkTemplate(payPalConnectUrl),
      learnLinkOpen: linkTemplate(payPalLearnUrl),
      linkClose: '</a>',
    }}
  />;
}

function PaymentContentMobile({ isStandalonePaypal }) {
  const reverbPaymentsLearnUrl = useHelpCenterArticleUrl(REVERB_PAYMENTS_HELP_ARTICLE_ID);
  const payPalLearnUrl = useHelpCenterArticleUrl(PAYPAL_PAYMENT_HELP_ARTICLE_ID);

  const learnUrl = isStandalonePaypal ? payPalLearnUrl : reverbPaymentsLearnUrl;

  return <I18N
    html
    text="discovery.ordersBanners.paymentMobileContent"
    args={{
      learnLinkOpen: linkTemplate(learnUrl),
      linkClose: '</a>',
    }}
  />;
}

function PaymentContent({ isStandalonePaypal, isMobile }) {
  const PaymentContentDesktop = isStandalonePaypal ? PayPalPaymentContent : ReverbPaymentsContent;

  return isMobile ? <PaymentContentMobile isStandalonePaypal={isStandalonePaypal} /> : <PaymentContentDesktop />;
}

export function OrdersBanners({
  isStandalonePaypal = false,
  showShippingBanner = true,
  showPaymentBanner = true,
}: {
  isStandalonePaypal: boolean;
  showShippingBanner: boolean;
  showPaymentBanner: boolean;
}) {
  const [shouldShowShippingBanner, setShouldShowShippingBanner] = React.useState(() => {
    return showShippingBanner &&
      localStorage.getItem(ORDERS_SHIPPING_BANNER_DISMISSED_KEY) !== 'true';
  });

  const [shouldShowPaymentBanner, setShouldShowPaymentBanner] = React.useState(() => {
    return showPaymentBanner &&
      localStorage.getItem(ORDERS_PAYMENT_BANNER_DISMISSED_KEY) !== 'true';
  });

  const { shopCountryCode } = useSeller();
  const isMobile = useMediaQuery('mobile');

  function handleShippingBannerClose() {
    setShouldShowShippingBanner(false);
    localStorage.setItem(ORDERS_SHIPPING_BANNER_DISMISSED_KEY, 'true');
  }

  function handlePaymentBannerClose() {
    setShouldShowPaymentBanner(false);
    localStorage.setItem(ORDERS_PAYMENT_BANNER_DISMISSED_KEY, 'true');
  }

  const isUKReverbShipping = shopCountryCode === COUNTRY_CODES.UK && !isStandalonePaypal;

  return (
    <div className="d-flex mobile-fx-dir-col orders-banners">
      {shouldShowShippingBanner &&
        <div className="orders-banners__banner">
          <RCCallout
            themeType="utility"
            variant="neutral-ultralight"
            icon={isUKReverbShipping ? undefined : <PackageIcon weight="duotone" className="fg-color-accent-notice" />}
            themedIcon={false}
            image={isUKReverbShipping ? royalMailLogo : undefined}
            imageSize={isUKReverbShipping ? 'small' : undefined}
            title={shippingTitle({ shopCountryCode, isMobile, isUKReverbShipping })}
            onDismiss={handleShippingBannerClose}
          >
            <ShippingContent isUKReverbShipping={isUKReverbShipping} shopCountryCode={shopCountryCode} isMobile={isMobile} />
          </RCCallout>
        </div>
      }

      {shouldShowPaymentBanner &&
        <div className="orders-banners__banner">
          <RCCallout
            themeType="utility"
            variant="info-ultralight"
            icon={<QuestionIcon />}
            title={isMobile ? undefined : t('paymentTitle')}
            onDismiss={handlePaymentBannerClose}
          >
            <PaymentContent isStandalonePaypal={isStandalonePaypal} isMobile={isMobile} />
          </RCCallout>
        </div>
      }
    </div>
  );
}
